'use client';

import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  PhysicalStage,
  RegistrationStage,
  RegistrationStageStatus,
  RegistrationStatus
} from '../types';

export const STAGE_PILL_STYLES: Record<RegistrationStage, string> = {
  // External lab
  registration:
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  sticking: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
  review:
    'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  ready_for_packing:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  // Internal lab
  reception: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
  sorting:
    'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
  handover_scanning:
    'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-300',
  photo_scanning:
    'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
  qr_review:
    'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  warehouse: 'bg-lime-100 text-lime-700 dark:bg-lime-900 dark:text-lime-300',
  ocr: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300',
  qa1_ocr:
    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
  qa2_ocr:
    'bg-indigo-200 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200',
  qa3_ocr:
    'bg-indigo-300 text-indigo-900 dark:bg-indigo-700 dark:text-indigo-100',
  // Terminal
  archived: 'bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-300'
};

export const STATUS_BADGE_VARIANT: Record<
  RegistrationStatus,
  'default' | 'secondary' | 'outline'
> = {
  registered: 'outline',
  active: 'default',
  closed: 'secondary'
};

export function StagePill({
  stage,
  label
}: {
  stage: RegistrationStage | 'shipping';
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-block rounded-full px-2.5 py-0.5 text-xs font-medium',
        getEffectiveStagePillStyle(stage)
      )}
    >
      {label}
    </span>
  );
}

export function StatusBadge({
  status,
  label
}: {
  status: RegistrationStatus;
  label: string;
}) {
  return (
    <Badge variant={STATUS_BADGE_VARIANT[status] ?? 'outline'}>{label}</Badge>
  );
}

const AWAITING_STYLE =
  'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200';
const ASSIGNED_STYLE =
  'bg-indigo-100 text-indigo-800 hover:bg-indigo-100 dark:bg-indigo-900 dark:text-indigo-200';
const IN_PROGRESS_STYLE =
  'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200';
const COMPLETED_STYLE =
  'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200';
const RETURNED_STYLE =
  'bg-rose-100 text-rose-800 hover:bg-rose-100 dark:bg-rose-900 dark:text-rose-200';

const STAGE_STATUS_STYLES: Partial<Record<RegistrationStageStatus, string>> = {
  // External lab
  creating:
    'bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200',
  returned_for_correction: RETURNED_STYLE,
  returned_from_sticking: RETURNED_STYLE,
  ready_for_numbering: ASSIGNED_STYLE,
  ready_for_printing: ASSIGNED_STYLE,
  ready_for_review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  returned_for_review: RETURNED_STYLE,
  reviewed: COMPLETED_STYLE,
  ready_for_packing:
    'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-200',
  packing: IN_PROGRESS_STYLE,
  packed: COMPLETED_STYLE,
  awaiting_shipment_assignment: AWAITING_STYLE,
  ready_for_shipping:
    'bg-cyan-100 text-cyan-800 hover:bg-cyan-100 dark:bg-cyan-900 dark:text-cyan-200',
  in_transport:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  // Internal lab — reception
  ready_for_reception: AWAITING_STYLE,
  received: COMPLETED_STYLE,
  // Internal lab — sorting
  ready_for_sorting: AWAITING_STYLE,
  sorting_in_progress: IN_PROGRESS_STYLE,
  sorted: COMPLETED_STYLE,
  // Internal lab — handover scanning
  ready_for_handover: AWAITING_STYLE,
  handed_over: COMPLETED_STYLE,
  // Internal lab — photo scanning (scan upload)
  scanning: IN_PROGRESS_STYLE,
  scanned: COMPLETED_STYLE,
  scan_returned_from_qr: RETURNED_STYLE,
  // Internal lab — QR review
  awaiting_qr_assignment: AWAITING_STYLE,
  qr_assigned: ASSIGNED_STYLE,
  qr_in_review: IN_PROGRESS_STYLE,
  qr_returned_from_scan: RETURNED_STYLE,
  ready_for_qr_review: AWAITING_STYLE,
  qr_reviewed: COMPLETED_STYLE,
  // Internal lab — warehouse
  ready_for_warehousing: AWAITING_STYLE,
  warehoused: COMPLETED_STYLE,
  // Internal lab — OCR
  processing: IN_PROGRESS_STYLE,
  ocr_completed: COMPLETED_STYLE,
  // Internal lab — QA OCR (tier 1)
  awaiting_qa1_assignment: AWAITING_STYLE,
  qa1_assigned: ASSIGNED_STYLE,
  qa1_in_review: IN_PROGRESS_STYLE,
  ready_for_qa1: AWAITING_STYLE,
  qa1_reviewed: COMPLETED_STYLE,
  // Internal lab — QA OCR (tier 2)
  awaiting_qa2_assignment: AWAITING_STYLE,
  qa2_assigned: ASSIGNED_STYLE,
  qa2_in_review: IN_PROGRESS_STYLE,
  qa2_returned_from_qa3: RETURNED_STYLE,
  ready_for_qa2: AWAITING_STYLE,
  qa2_reviewed: COMPLETED_STYLE,
  // Internal lab — QA OCR (tier 3)
  awaiting_qa3_assignment: AWAITING_STYLE,
  qa3_assigned: ASSIGNED_STYLE,
  qa3_in_review: IN_PROGRESS_STYLE,
  qa3_returned_from_client: RETURNED_STYLE,
  ready_for_qa3: AWAITING_STYLE,
  qa3_reviewed: COMPLETED_STYLE,
  // Repair sub-workflow (sorting / scan-upload / qr-review origins)
  returned_for_internal_repair: RETURNED_STYLE,
  returned_for_external_repair: RETURNED_STYLE,
  repairing: IN_PROGRESS_STYLE,
  returned_from_repair: RETURNED_STYLE
};

const PHYSICAL_STAGE_STYLES: Record<PhysicalStage, string> = {
  filling:
    'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200',
  shipping:
    'bg-cyan-100 text-cyan-800 hover:bg-cyan-100 dark:bg-cyan-900 dark:text-cyan-200',
  transport:
    'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200',
  internal_lab_sorting:
    'bg-violet-100 text-violet-800 hover:bg-violet-100 dark:bg-violet-900 dark:text-violet-200'
};

/**
 * Returns the effective stage to display in the "current stage" column.
 * A container technically still sits at `ready_for_packing` while its
 * box moves through the shipping pipeline, but the column should reflect
 * the real-world progress:
 *
 * - `stage_status = ready_for_shipping` → show "Shipping" (virtual stage)
 * - `stage_status = in_transport`       → show "Reception" — the shipment
 *   is en route to the receiving lab, so the next real stage label
 *   is what the user expects to see.
 */
export function getEffectiveStage(
  currentStage: RegistrationStage,
  stageStatus?: RegistrationStageStatus | null
): RegistrationStage | 'shipping' {
  if (currentStage === 'ready_for_packing') {
    if (stageStatus === 'ready_for_shipping') return 'shipping';
    if (stageStatus === 'in_transport') return 'reception';
  }
  return currentStage;
}

const VIRTUAL_STAGE_STYLES: Record<'shipping', string> = {
  shipping: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300'
};

export function getEffectiveStagePillStyle(
  stage: ReturnType<typeof getEffectiveStage>
): string {
  if (stage === 'shipping') {
    return VIRTUAL_STAGE_STYLES.shipping;
  }
  return STAGE_PILL_STYLES[stage] ?? STAGE_PILL_STYLES.registration;
}

/**
 * Sub-status cell for a registration row. Shows a single badge — the
 * `stage_status` when present, otherwise falls back to `physical_stage`.
 * The two fields used to render as separate stacked badges, which read
 * as redundant ("جاهزة للشحن" + "الشحن") and was the main source of the
 * "two badges for the same thing" UI noise.
 */
export function SubStatusCell({
  stageStatus,
  physicalStage
}: {
  stageStatus?: RegistrationStageStatus | null;
  physicalStage?: PhysicalStage | null;
}) {
  const t = useTranslations('registration');

  if (stageStatus) {
    const label = t(`stageStatus.${stageStatus}` as never) as string;
    return (
      <Badge
        variant='secondary'
        className={cn(STAGE_STATUS_STYLES[stageStatus])}
      >
        {label}
      </Badge>
    );
  }

  if (physicalStage) {
    const label = t(
      `physicalStage${physicalStage
        .split('_')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('')}` as never
    ) as string;
    return (
      <Badge
        variant='secondary'
        className={cn(PHYSICAL_STAGE_STYLES[physicalStage])}
      >
        {label}
      </Badge>
    );
  }

  return <span className='text-muted-foreground text-xs'>&mdash;</span>;
}
